import { Modal, Form, Input, InputNumber, DatePicker, Select, message, Row, Col, Typography, Divider, Button, Statistic } from "antd";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { useAuth } from "../../layout/auth";
import { PlusOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Link, Text } = Typography;

const AddPagoForm = ({ visible, setVisible, setPagos, loading, setLoading, initialValues, initialVerificacionId, onPaymentAdded }) => {
  const [form] = Form.useForm();
  const auth = useAuth();
  const [showOptional, setShowOptional] = useState(false);
  const [verificaciones, setVerificaciones] = useState([]);
  const [selectedVerificacionId, setSelectedVerificacionId] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue({
          ...initialValues,
          FechaPago: initialValues.FechaPago ? moment(initialValues.FechaPago) : null,
        });
        // Show optional fields if they have values
        if (initialValues.FacturaFolio || initialValues.ReferenciaPago || initialValues.Notas) {
          setShowOptional(true);
        }
      } else if (initialVerificacionId) {
        form.resetFields();
        form.setFieldsValue({
          VerificacionID: initialVerificacionId,
        });
        setSelectedVerificacionId(initialVerificacionId);
        setShowOptional(false);
      } else {
        form.resetFields();
        setShowOptional(false);
      }

      // Fetch verifications when the modal becomes visible
      axios
        .get(`${process.env.REACT_APP_API_URL}/verificaciones`, {
          params: { u: auth.UID },
        })
        .then((res) => {
          setVerificaciones(res.data);
        })
        .catch((err) => {
          message.error("Error al cargar verificaciones: " + err.message);
        });
    }
  }, [visible, initialValues, form, auth.UID, initialVerificacionId]);

  useEffect(() => {
    if (selectedVerificacionId) {
      axios
        .get(`${process.env.REACT_APP_API_URL}/pagos/summary/${selectedVerificacionId}`)
        .then((res) => {
          setSummaryData(res.data);
        })
        .catch((err) => {
          message.error("Error al cargar resumen de pagos: " + err.message);
          setSummaryData(null);
        });
    } else {
      setSummaryData(null);
    }
  }, [selectedVerificacionId]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (initialValues) {
        // Edit existing payment
        await axios.put(`${process.env.REACT_APP_API_URL}/pagos/${initialValues.PagoID}`, {
          FacturaFolio: values.FacturaFolio,
          Monto: values.Monto,
          Moneda: values.Moneda,
          Metodo: values.Metodo,
          FechaPago: values.FechaPago.format("YYYY-MM-DD"),
          VerificacionID: values.VerificacionID,
          ReferenciaPago: values.ReferenciaPago,
          Notas: values.Notas,
          u: auth.UID,
        });
        message.success("Pago actualizado exitosamente!");
      } else {
        // Add new payment
        await axios.post(`${process.env.REACT_APP_API_URL}/pagos`, {
          FacturaFolio: values.FacturaFolio,
          Monto: values.Monto,
          Moneda: values.Moneda,
          Metodo: values.Metodo,
          FechaPago: values.FechaPago.format("YYYY-MM-DD"),
          VerificacionID: values.VerificacionID,
          ReferenciaPago: values.ReferenciaPago,
          Notas: values.Notas,
          u: auth.UID,
        });
        message.success("Pago agregado exitosamente!");
      }

      form.resetFields();
      setVisible(false);

      // This part should ideally be handled by the onPaymentAdded callback
      // const res = await axios.get(`${process.env.REACT_APP_API_URL}/pagos`, {
      //   params: { u: auth.UID },
      // });
      // setPagos(res.data);
      if (setPagos) {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/pagos`, {
          params: { u: auth.UID },
        });
        setPagos(res.data);
      }

      if (onPaymentAdded) {
        onPaymentAdded();
      }

    } catch (error) {
      message.error("Error al guardar pago: " + error.message);
    }
    setLoading(false);
  };

  return (
    <Modal
      visible={visible}
      title={initialValues ? "Editar Pago" : "Agregar Nuevo Pago"}
      okText={initialValues ? "Guardar Cambios" : "Agregar"}
      cancelText="Cancelar"
      onCancel={() => {
        setVisible(false);
        form.resetFields();
        setShowOptional(false);
      }}
      onOk={() => {
        form.validateFields().then(onFinish);
      }}
      confirmLoading={loading}
      width={800}
    >
      <Form form={form} layout="vertical" name="add_pago_form">
        <Col span={6}>
          <Form.Item name="VerificacionID" label="Folio de Verificación" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Seleccione un folio"
              optionFilterProp="children"
              filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              onChange={(value) => setSelectedVerificacionId(value)}
              disabled={!!initialVerificacionId}
            >
              {verificaciones.map((v) => (
                <Select.Option key={v.VerificacionID} value={v.VerificacionID}>
                  {v.ControlInterno}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {summaryData && (
          <Col span={18}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Monto Total:</Text> ${summaryData.MontoTotal?.toFixed(2) || '0.00'}
              <Divider type="vertical" />
              <Text strong>Monto Pagado:</Text> ${summaryData.MontoPagado?.toFixed(2) || '0.00'}
              <Divider type="vertical" />
              <Text strong>Balance:</Text> ${summaryData.Balance?.toFixed(2) || '0.00'}
            </div>
          </Col>
        )}
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="FechaPago" label="Fecha" rules={[{ required: true, message: "Seleccione la fecha" }]}>
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="Monto" label="Monto" rules={[{ required: true, message: "Ingrese monto" }]}>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="Moneda" label="Moneda" rules={[{ required: true, message: "Seleccione moneda" }]}>
              <Select>
                <Select.Option value="MXN">MXN</Select.Option>
                <Select.Option value="USD">USD</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="Metodo" label="Método" rules={[{ required: true, message: "Seleccione método" }]}>
              <Select>
                <Select.Option value="Transferencia">Transferencia</Select.Option>
                <Select.Option value="Efectivo">Efectivo</Select.Option>
                <Select.Option value="Cheque">Cheque</Select.Option>
                <Select.Option value="Tarjeta">Tarjeta</Select.Option>
                <Select.Option value="Otro">Otro</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Optional fields toggle */}
        <div style={{ margin: "8px 0 16px" }}>
          <Link onClick={() => setShowOptional(!showOptional)}>
            {showOptional ? "Ocultar campos opcionales" : "Mostrar campos opcionales"}
          </Link>
        </div>

        {/* Optional fields */}
        {showOptional && (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="FacturaFolio" label="Folio de Factura" rules={[{ required: false }]}>
                  <Input placeholder="Ej. F1234" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="ReferenciaPago" label="Referencia de Pago" rules={[{ required: false }]}>
                  <Input placeholder="Referencia o concepto" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="Notas" label="Notas" rules={[{ required: false }]}>
              <TextArea rows={3} placeholder="Comentarios adicionales" maxLength={500} style={{ resize: "vertical" }} />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AddPagoForm;